import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useListNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  type PortalNotificationQueryArgs,
} from '../state/domainRouterApi';
import { useAppDispatch } from '../state/hooks';
import { retainYstnjAssistantRealtimeBridge } from '../state/assistantRealtimeBridge';
import { store, type RootState } from '../state/store';
import type { NotificationFeedItem } from './types';

export type UseNotificationsCenterArgs = {
  enabled: boolean;
  participantId?: string | undefined;
  familyId?: string | undefined;
};

export type UseNotificationsCenterResult = {
  items: NotificationFeedItem[];
  unreadCount: number;
  isLoading: boolean;
  markingNotificationId: string | null;
  error: string | null;
  refreshedAt: number | null;
  refresh: () => void;
  markNotificationRead: (notificationId: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
};

const toErrorMessage = (error: unknown): string => {
  if (!error) return 'Unknown error';
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && 'message' in (error as Record<string, unknown>)) {
    return String((error as Record<string, unknown>).message ?? 'Unknown error');
  }
  return String(error);
};

const toEpochMilliseconds = (value: unknown): number => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  return Date.now();
};

const sortNotificationFeedItems = (items: NotificationFeedItem[]): NotificationFeedItem[] => (
  [...items].sort((left, right) => {
    if (left.isUnread !== right.isUnread) {
      return left.isUnread ? -1 : 1;
    }
    return right.lastUpdatedAt - left.lastUpdatedAt;
  })
);

const PRELOADED_NOTIFICATION_PAGE_SIZE = 5;

const toNotificationFeedItems = (
  items: unknown[],
  optimisticReadIds: ReadonlySet<string>,
): NotificationFeedItem[] => items
  .map((raw): NotificationFeedItem => {
    const item = (raw ?? {}) as Record<string, unknown>;
    const targetRef = (item.targetRef ?? {}) as Record<string, unknown>;
    const actorState = (item.actorState ?? {}) as Record<string, unknown>;
    const id = String(item.id ?? '');
    const deliveryState = String(item.deliveryState ?? 'unread').toLowerCase();
    const persistedUnreadCount = Number(
      actorState.unreadCount
      ?? (deliveryState === 'read' ? 0 : 1)
    );
    const isOptimisticallyRead = optimisticReadIds.has(id);
    const unreadCount = isOptimisticallyRead ? 0 : Math.max(0, persistedUnreadCount);
    const isUnread = unreadCount > 0 || (!isOptimisticallyRead && deliveryState !== 'read');
    return {
      id,
      subject: String(item.title ?? item.subject ?? 'Notification'),
      status: isUnread ? 'unread' : 'read',
      isUnread,
      familyId: String(targetRef.familyId ?? ''),
      applicationId: targetRef.applicationId == null ? null : String(targetRef.applicationId),
      unreadCount,
      lastUpdatedAt: toEpochMilliseconds(item.updatedAt ?? item.createdAt ?? 0),
    };
  })
  .filter((item) => item.id.length > 0);

/**
 * Deprecated note: the legacy `notification-center` event-registry transport remains
 * in-tree for legacy support only. This hook is now RTK-first + realtime-bridge driven.
 */
export const useNotificationsCenter = ({
  enabled,
  participantId,
  familyId,
}: UseNotificationsCenterArgs): UseNotificationsCenterResult => {
  const dispatch = useAppDispatch();
  const [markingNotificationId, setMarkingNotificationId] = useState<string | null>(null);
  const [optimisticReadIds, setOptimisticReadIds] = useState<string[]>([]);

  const hasIdentityFilter = Boolean(participantId || familyId);

  const listQueryArgs = useMemo<PortalNotificationQueryArgs>(() => ({
    actorId: participantId,
    familyId,
    includeRead: true,
    limit: PRELOADED_NOTIFICATION_PAGE_SIZE,
  }), [familyId, participantId]);

  const shouldSkip = !enabled || !hasIdentityFilter;
  const notificationsQuery = useListNotificationsQuery(listQueryArgs, {
    skip: shouldSkip,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const [markNotificationReadMutation] = useMarkNotificationReadMutation();
  const [markAllNotificationsReadMutation] = useMarkAllNotificationsReadMutation();

  useEffect(() => {
    if (!enabled || !participantId) {
      return undefined;
    }

    const subscription = retainYstnjAssistantRealtimeBridge({
      dispatch,
      getState: store.getState as () => RootState,
      actorId: participantId,
      familyId,
      listQueryArgs,
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch, enabled, familyId, listQueryArgs, participantId]);

  const refresh = useCallback(() => {
    if (shouldSkip) return;
    void notificationsQuery.refetch();
  }, [notificationsQuery, shouldSkip]);

  const markNotificationRead = useCallback(async (notificationId: string) => {
    if (!notificationId || shouldSkip) return;

    try {
      setMarkingNotificationId(notificationId);
      setOptimisticReadIds((current) => (
        current.includes(notificationId) ? current : [...current, notificationId]
      ));
      await markNotificationReadMutation({
        ...listQueryArgs,
        id: notificationId,
        actorId: participantId,
      }).unwrap();
      await notificationsQuery.refetch().catch(() => undefined);
    } catch (error) {
      setOptimisticReadIds((current) => current.filter((id) => id !== notificationId));
      throw error;
    } finally {
      setMarkingNotificationId(null);
    }
  }, [listQueryArgs, markNotificationReadMutation, notificationsQuery, participantId, shouldSkip]);

  const markAllNotificationsRead = useCallback(async () => {
    if (shouldSkip) return;
    const unreadIds = Array.isArray(notificationsQuery.data?.items)
      ? (notificationsQuery.data.items as Array<Record<string, unknown>>)
        .map((item) => String(item?.id ?? '').trim())
        .filter(Boolean)
      : [];
    if (unreadIds.length > 0) {
      setOptimisticReadIds((current) => [...new Set([...current, ...unreadIds])]);
    }
    await markAllNotificationsReadMutation({
      ...listQueryArgs,
      actorId: participantId,
      familyId,
    }).unwrap();
    await notificationsQuery.refetch().catch(() => undefined);
  }, [familyId, listQueryArgs, markAllNotificationsReadMutation, notificationsQuery, participantId, shouldSkip]);

  useEffect(() => {
    if (optimisticReadIds.length === 0) {
      return;
    }
    const rawItems = Array.isArray(notificationsQuery.data?.items)
      ? (notificationsQuery.data.items as Array<Record<string, unknown>>)
      : [];
    const itemIds = new Set(rawItems.map((item) => String(item?.id ?? '').trim()).filter(Boolean));
    const nextPending = optimisticReadIds.filter((notificationId) => {
      const item = rawItems.find((candidate) => String(candidate?.id ?? '').trim() === notificationId);
      if (!item) {
        return itemIds.has(notificationId);
      }
      const actorState = (item.actorState ?? {}) as Record<string, unknown>;
      const deliveryState = String(item.deliveryState ?? 'unread').toLowerCase();
      const unreadCount = Number(actorState.unreadCount ?? (deliveryState === 'read' ? 0 : 1));
      return unreadCount > 0 && deliveryState !== 'read';
    });
    if (nextPending.length !== optimisticReadIds.length) {
      setOptimisticReadIds(nextPending);
    }
  }, [notificationsQuery.data, optimisticReadIds]);

  if (!enabled) {
    return {
      items: [],
      unreadCount: 0,
      isLoading: false,
      markingNotificationId,
      error: null,
      refreshedAt: null,
      refresh,
      markNotificationRead,
      markAllNotificationsRead,
    };
  }

  if (!hasIdentityFilter) {
    return {
      items: [],
      unreadCount: 0,
      isLoading: false,
      markingNotificationId,
      error: null,
      refreshedAt: null,
      refresh,
      markNotificationRead,
      markAllNotificationsRead,
    };
  }

  const rawItems = Array.isArray(notificationsQuery.data?.items)
    ? notificationsQuery.data.items
    : [];
  const items = sortNotificationFeedItems(
    toNotificationFeedItems(rawItems as unknown[], new Set(optimisticReadIds))
  );
  const unreadCount = items.reduce((sum, item) => sum + Math.max(0, item.unreadCount), 0);
  const refreshedAt = notificationsQuery.fulfilledTimeStamp ?? null;

  const queryError = notificationsQuery.error ? toErrorMessage(notificationsQuery.error) : null;

  return {
    items,
    unreadCount,
    isLoading: notificationsQuery.isLoading,
    markingNotificationId,
    error: queryError,
    refreshedAt,
    refresh,
    markNotificationRead,
    markAllNotificationsRead,
  };
};
